export const PUBLIC_REGISTRATION_SESSION_STORAGE_KEY = "safetyqr_public_session_id"
export const OWNER_PORTAL_SESSION_STORAGE_KEY = "safetyqrOwnerPortalSession"
export const OWNER_PORTAL_DEVICE_STORAGE_KEY = "safetyqrOwnerPortalDeviceId"

export type PublicTagType =
  | "fleet_tag"
  | "personal_tag"
  | "vehicle_sticker"
  | null

export interface RegistrationSessionSnapshot {
  sessionId: string | null
}

export interface OwnerPortalSession {
  expiresAt: string | null
  expiresInSec: number | null
  kitId: string | null
  phone: string | null
  stickerId: string | null
  tagType: PublicTagType
  token: string
  tokenType: string
  verifiedAt: string | null
}

export interface OwnerPortalSessionSnapshot {
  deviceId: string | null
  session: OwnerPortalSession | null
}

export interface PersistOwnerPortalSessionInput {
  expiresAt?: string | null
  expiresInSec?: number | null
  kitId?: string | null
  phone?: string | null
  stickerId?: string | null
  tagType?: PublicTagType
  token: string
  tokenType?: string
  verifiedAt?: string | null
}

export type OwnerPortalSessionRestoreResult =
  | {
      deviceId: string
      session: OwnerPortalSession
      status: "restored"
    }
  | {
      deviceId: string
      session: null
      status: "expired" | "invalid" | "missing"
    }

function normalizeTagType(value: unknown): PublicTagType {
  if (value === "fleet_tag" || value === "personal_tag" || value === "vehicle_sticker") {
    return value
  }

  return null
}

function isBrowser() {
  return typeof window !== "undefined"
}

let cachedRegistrationSessionId: string | null | undefined

export function getRegistrationSessionSnapshot(): RegistrationSessionSnapshot {
  if (!isBrowser()) {
    return {
      sessionId: null,
    }
  }

  return {
    sessionId: window.localStorage.getItem(PUBLIC_REGISTRATION_SESSION_STORAGE_KEY),
  }
}

export function getOrCreateRegistrationSessionId(): string {
  if (!isBrowser()) {
    return `public-${Date.now()}`
  }

  if (cachedRegistrationSessionId) {
    return cachedRegistrationSessionId
  }

  const existingSessionId = window.localStorage.getItem(
    PUBLIC_REGISTRATION_SESSION_STORAGE_KEY
  )

  if (existingSessionId) {
    cachedRegistrationSessionId = existingSessionId
    return existingSessionId
  }

  const nextSessionId = `public-${window.crypto.randomUUID()}`
  window.localStorage.setItem(PUBLIC_REGISTRATION_SESSION_STORAGE_KEY, nextSessionId)
  cachedRegistrationSessionId = nextSessionId

  return nextSessionId
}

export function getOwnerPortalSessionSnapshot(): OwnerPortalSessionSnapshot {
  if (!isBrowser()) {
    return {
      deviceId: null,
      session: null,
    }
  }

  return {
    deviceId: window.localStorage.getItem(OWNER_PORTAL_DEVICE_STORAGE_KEY),
    session: readPersistedOwnerPortalSession(),
  }
}

export function getOrCreateOwnerPortalDeviceId(): string {
  if (!isBrowser()) {
    return `owner-${Date.now()}`
  }

  const existingDeviceId = window.localStorage.getItem(OWNER_PORTAL_DEVICE_STORAGE_KEY)

  if (existingDeviceId) {
    return existingDeviceId
  }

  const nextDeviceId = `owner-${window.crypto.randomUUID()}`
  window.localStorage.setItem(OWNER_PORTAL_DEVICE_STORAGE_KEY, nextDeviceId)

  return nextDeviceId
}

export function restoreOwnerPortalSession(): OwnerPortalSessionRestoreResult {
  const deviceId = getOrCreateOwnerPortalDeviceId()
  const hadRawSession = isBrowser()
    ? Boolean(window.sessionStorage.getItem(OWNER_PORTAL_SESSION_STORAGE_KEY))
    : false
  const session = readPersistedOwnerPortalSession()

  if (!session) {
    clearOwnerPortalSession()

    return {
      deviceId,
      session: null,
      status: hadRawSession ? "invalid" : "missing",
    }
  }

  const expiresAtMs = getOwnerPortalSessionExpiryMs(session)

  if (expiresAtMs === null) {
    clearOwnerPortalSession()

    return {
      deviceId,
      session: null,
      status: "invalid",
    }
  }

  if (expiresAtMs <= Date.now()) {
    clearOwnerPortalSession()

    return {
      deviceId,
      session: null,
      status: "expired",
    }
  }

  return {
    deviceId,
    session,
    status: "restored",
  }
}

export function persistOwnerPortalSession(
  input: PersistOwnerPortalSessionInput
): OwnerPortalSession {
  const session: OwnerPortalSession = {
    expiresAt: input.expiresAt?.trim() || null,
    expiresInSec:
      typeof input.expiresInSec === "number" && Number.isFinite(input.expiresInSec)
        ? input.expiresInSec
        : null,
    kitId: normalizeOptionalString(input.kitId),
    phone: normalizeOptionalString(input.phone),
    stickerId: normalizeOptionalString(input.stickerId),
    tagType: normalizeTagType(input.tagType),
    token: input.token.trim(),
    tokenType: input.tokenType?.trim() || "Bearer",
    verifiedAt: input.verifiedAt?.trim() || new Date().toISOString(),
  }

  if (isBrowser()) {
    window.sessionStorage.setItem(
      OWNER_PORTAL_SESSION_STORAGE_KEY,
      JSON.stringify(session)
    )
  }

  return session
}

export function clearOwnerPortalSession() {
  if (!isBrowser()) {
    return
  }

  window.sessionStorage.removeItem(OWNER_PORTAL_SESSION_STORAGE_KEY)
}

export function isOwnerPortalSessionExpired(session: OwnerPortalSession): boolean {
  const expiresAtMs = getOwnerPortalSessionExpiryMs(session)

  return expiresAtMs === null || expiresAtMs <= Date.now()
}

export function getOwnerPortalSessionExpiryMs(
  session: OwnerPortalSession
): number | null {
  if (session.expiresAt) {
    const expiresAtMs = new Date(session.expiresAt).getTime()

    if (Number.isFinite(expiresAtMs)) {
      return expiresAtMs
    }
  }

  if (session.verifiedAt && session.expiresInSec) {
    const verifiedAtMs = new Date(session.verifiedAt).getTime()

    if (Number.isFinite(verifiedAtMs)) {
      return verifiedAtMs + session.expiresInSec * 1000
    }
  }

  return null
}

export function getOwnerPortalAuthorizationHeader(): string | null {
  const session = readPersistedOwnerPortalSession()

  if (!session || isOwnerPortalSessionExpired(session)) {
    clearOwnerPortalSession()
    return null
  }

  return `${session.tokenType} ${session.token}`
}

function normalizeOptionalString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }

  const normalized = value.trim()
  return normalized || null
}

function readPersistedOwnerPortalSession(): OwnerPortalSession | null {
  if (!isBrowser()) {
    return null
  }

  const rawSession = window.sessionStorage.getItem(OWNER_PORTAL_SESSION_STORAGE_KEY)

  if (!rawSession) {
    return null
  }

  try {
    const parsedSession = JSON.parse(rawSession) as PersistOwnerPortalSessionInput

    if (typeof parsedSession.token !== "string" || !parsedSession.token.trim()) {
      return null
    }

    return {
      expiresAt: normalizeOptionalString(parsedSession.expiresAt),
      expiresInSec:
        typeof parsedSession.expiresInSec === "number"
        && Number.isFinite(parsedSession.expiresInSec)
          ? parsedSession.expiresInSec
          : null,
      kitId: normalizeOptionalString(parsedSession.kitId),
      phone: normalizeOptionalString(parsedSession.phone),
      stickerId: normalizeOptionalString(parsedSession.stickerId),
      tagType: normalizeTagType(parsedSession.tagType),
      token: parsedSession.token.trim(),
      tokenType: normalizeOptionalString(parsedSession.tokenType) ?? "Bearer",
      verifiedAt: normalizeOptionalString(parsedSession.verifiedAt),
    }
  } catch {
    return null
  }
}
